import { useEffect, useRef } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { useTranslation } from 'react-i18next';
import WhatsAppConnectAdvisorHelp from 'src/components/WhatsAppConnectAdvisorHelp/WhatsAppConnectAdvisorHelp.component';
import { resolveEmbeddedSignupStepGuidance } from 'src/constants/embeddedSignupSteps.constants';
import type { tWhatsAppConnectAdvisorHelpTranslations } from 'src/components/WhatsAppConnectAdvisorHelp/WhatsAppConnectAdvisorHelp.types';
import {
  formatMetaReportedMessage,
  hasReportedSignupStep,
} from '../WhatsAppConnect.functions';
import type { tConnectionEscalatedProps } from './ConnectionEscalated.types';
import '../WhatsAppConnect.shared.styles.scss';

const DEFAULT_NAMESPACE = 'whatsappConnect/whatsappConnect';

function buildAdvisorTranslations(
  t: ReturnType<typeof useTranslation>['t'],
): tWhatsAppConnectAdvisorHelpTranslations {
  return {
    liveTitle: t('advisorHelp.live.title', '¿Tuviste problemas con Meta?'),
    liveText: t(
      'advisorHelp.live.text',
      'Hay un asesor en sesión EN VIVO AHORA listo para ayudarte a conectar tu WhatsApp paso a paso.',
    ),
    livePrimaryCta: t('advisorHelp.live.primaryCta', 'Unirme a la sesión en vivo'),
    liveSecondaryText: t('advisorHelp.live.secondaryText', '¿Prefieres otro momento?'),
    liveSecondaryCta: t('advisorHelp.live.secondaryCta', 'Agendar con un asesor'),
    offlineTitle: t('advisorHelp.offline.title', '¿Tuviste problemas con Meta?'),
    offlineText: t(
      'advisorHelp.offline.text',
      'Las sesiones en vivo con un asesor están disponibles Lunes a Viernes de 9:00 a 18:00 (hora CDMX). Mientras tanto, puedes agendar una sesión:',
    ),
    offlinePrimaryCta: t('advisorHelp.offline.primaryCta', 'Agendar con un asesor'),
    offlineHoursInfo: t(
      'advisorHelp.offline.hoursInfo',
      'Lunes a Viernes de 9:00 a 18:00 (hora CDMX)',
    ),
  };
}

export default function ConnectionEscalated(props: tConnectionEscalatedProps) {
  const { t } = useTranslation(props.translationNamespace ?? DEFAULT_NAMESPACE);
  const onMountRef = useRef(props.onMount);
  onMountRef.current = props.onMount;
  // One-shot guard: React 18 StrictMode runs useEffect twice in dev, which
  // would otherwise double-fire the escalated_viewed analytics event.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onMountRef.current?.();
  }, []);

  const advisorTranslations = props.advisorTranslations ?? buildAdvisorTranslations(t);

  const stepReported = hasReportedSignupStep(props.signupStep);
  const guidance = resolveEmbeddedSignupStepGuidance(props.signupStep);
  const metaMessage = formatMetaReportedMessage(props.signupErrorReport?.message);
  const showDiagnosis = stepReported || metaMessage !== null;

  function handleAdvisorClick(kind: 'live' | 'schedule') {
    props.onAdvisorCtaClick?.(kind);
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col items-center justify-center gap-6 py-10 text-center flex-1">
        <LockClosedIcon className="w-16 h-16 text-warning" aria-hidden="true" />

        <div className="flex flex-col gap-2 max-w-lg">
          <h2 className="text-2xl font-semibold text-base-content">
            {t('escalated.title', 'Meta informó que tu número alcanzó el límite de intentos')}
          </h2>
          <p className="text-base text-base-content/70">
            {t(
              'escalated.disclaimer',
              'Meta informó que se alcanzó el límite de intentos de registro o verificación de este número de teléfono. Un asesor puede ayudarte a revisar qué hacer para continuar.',
            )}
          </p>
        </div>

        {showDiagnosis && (
          <div className="flex flex-col gap-1 max-w-xl w-full px-4 py-3 rounded-lg border border-base-300 bg-base-200/40 text-left">
            <span className="text-sm font-medium text-base-content">
              {stepReported
                ? t(guidance.titleKey, guidance.titleDefault)
                : t('failedClosed.metaReported', 'Esto es lo que Meta nos informó:')}
            </span>
            <span className="text-sm text-base-content/70">
              {metaMessage ?? t(guidance.subtitleKey, guidance.subtitleDefault)}
            </span>
          </div>
        )}

        <div className="max-w-xl w-full">
          <WhatsAppConnectAdvisorHelp
            translations={advisorTranslations}
            onLiveClick={() => handleAdvisorClick('live')}
            onScheduleClick={() => handleAdvisorClick('schedule')}
          />
        </div>
      </div>

      <div className="WhatsAppConnect__actions">
        <Button color="tertiary" size="md" type="button" onClick={props.onBack}>
          {t('footer.back', 'Regresar')}
        </Button>
      </div>
    </div>
  );
}
